import type CreativeType from '@/Enums/Creatives/CreativeType.enum';
import { ICreativeFilters } from '@/Interfaces/Creatives/Creative.interface';
import { Grid } from '@/components/Grid';
import ServerSidePagination from '@/components/Grid/Pagination/ServerSidePagination';
import {
  GridPaginationAndSortFilters,
  useGridApiPaginationAndSort,
} from '@/components/Grid/hooks/useGridApiPaginationAndSort';
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import type { SxProps, Theme } from '@mui/material';
import { GridColDef, GridRowSelectionModel, GridValidRowModel } from '@mui/x-data-grid-pro';
import { memo, useCallback, useEffect } from 'react';

interface IProps {
  loading?: boolean;
  rows: GridValidRowModel[];
  rowCount?: number;
  columns: GridColDef[];
  checkboxSelection?: boolean;
  rowSelectionModel: GridRowSelectionModel;
  withoutPagination?: boolean;
  onRowSelectionModelChange: (model: GridRowSelectionModel) => void;
  onFiltersChange: (filters: Partial<ICreativeFilters>) => void;
  selectedCreativesTypes?: CreativeType[];
  keepNonExistentRowsSelected?: boolean;
  sx?: SxProps<Theme>;
}

const CreativesGrid = memo(
  ({
    loading,
    rows,
    rowCount,
    columns,
    checkboxSelection,
    rowSelectionModel,
    withoutPagination,
    onRowSelectionModelChange,
    selectedCreativesTypes,
    onFiltersChange,
    keepNonExistentRowsSelected,
    sx,
  }: IProps) => {
    const setTableFilters = useCallback(
      (filters: GridPaginationAndSortFilters) => onFiltersChange(filters),
      [onFiltersChange],
    );

    const { paginationModel, sortModel, handlePaginationModelChange, handleSortModelChange, handleReset } =
      useGridApiPaginationAndSort({ setFilters: setTableFilters });

    useEffect(() => {
      handleReset();
    }, [handleReset, selectedCreativesTypes]);

    return (
      <Grid
        loading={loading}
        checkboxSelection={checkboxSelection}
        rows={rows}
        columns={columns}
        columnHeaderHeight={40}
        rowHeight={70}
        disableRowSelectionOnClick
        disableColumnMenu
        disableColumnFilter
        pagination={!withoutPagination}
        rowThreshold={0}
        columnThreshold={0}
        rowSelection
        onRowSelectionModelChange={onRowSelectionModelChange}
        rowSelectionModel={rowSelectionModel}
        showCellVerticalBorder
        showColumnVerticalBorder
        disableCellSelection
        keepNonExistentRowsSelected={keepNonExistentRowsSelected}
        slots={{
          pagination: ServerSidePagination,
          columnSortedAscendingIcon: KeyboardArrowUp,
          columnSortedDescendingIcon: KeyboardArrowDown,
        }}
        scrollbarSize={0}
        slotProps={{
          pagination: {
            // @ts-expect-error doesn't recognize rowCount
            rowCount,
            loading,
          },
        }}
        rowCount={rowCount}
        paginationMode={withoutPagination ? 'client' : 'server'}
        paginationModel={paginationModel}
        onPaginationModelChange={handlePaginationModelChange}
        sortingMode='server'
        sortModel={sortModel}
        onSortModelChange={handleSortModelChange}
        hideFooter={withoutPagination}
        sx={sx}
      />
    );
  },
);

export default CreativesGrid;
